<template>
  <div class="flex justify-end h-10 p-1 border-t border-b">
    <button
      v-if="mode == 'edit' && !priceCatalogOrder?.deleted"
      type="button"
      class="px-2 py-1 border border-gray-300 rounded-md"
      @click="modalAddCatalog = true">
      <span class="flex items-center gap-2 text-sm capitalize text-theme-500">
        <IconAddSmall />
        <p>{{ $t('common.actions.addNew') }}</p>
      </span>
    </button>
  </div>
  <PerfectScrollbar class="h-full pb-1.5">
    <draggable
      :disabled="mode == 'view'"
      v-model="priceCatalogOrderDetailItems"
      chosen-class="chosen"
      animation="300"
      item-key="id"
      @end="onDragEnd">
      <template #item="{ element, index }">
        <div class="draggable-item">
          <div class="flex items-center gap-2">
            <div
              class="flex items-center justify-center w-6 h-6 ml-2 text-xs rounded-full bg-slate-200">
              <p>{{ index }}</p>
            </div>

            <div :class="variantClass(element.deleted)">
              <div class="flex gap-2">
                <div class="w-4 rounded-md bg-cyan-light" />
                <span
                  class="py-2 text-sm font-light -ms-3 ps-2 text-slate-900"
                  :class="element.deleted ? 'bg-red-100' : 'bg-white'">
                  {{ element.priceCatalog.name }}
                </span>
              </div>
              <div class="flex items-center">
                <span class="mr-6 text-sm font-light text-slate-900">
                  {{ element.priceCatalog.code }}
                </span>
                <button
                  type="button"
                  class="hidden"
                  :class="
                    mode == 'edit' && !element.deleted
                      ? 'hidden hover:text-red-400 group-hover:block'
                      : ''
                  "
                  @click="emit('detailDeleted', element)">
                  <IconTrashOutline class="size-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </template>
    </draggable>
  </PerfectScrollbar>

  <BaseModalEditCreate
    v-if="modalAddCatalog && priceCatalogOrder"
    mode="select"
    :is-modified="false"
    :title="$t('pricing-catalogs.addNewCatalogTitle')"
    max-width="compact"
    @dismiss-changes="modalAddCatalog = false"
    @save="emitSelectedRows = true">
    <template #form>
      <div>
        <label class="px-1 text-sm font-normal">CTRL + Click to select</label>
        <BaseTable
          :headers="priceCatalogsHeaders"
          :list="unusedPriceCatalogs"
          :is-add-new-hidden="true"
          :hide-multiselect-delete="true"
          :hide-multiselect-download="true"
          :hide-grouping="true"
          :tableControlsHidden="true"
          @selected-rows="onCatalogsSelected"
          table-id="priceCatalogsSelect"
          :emit-selected-rows="emitSelectedRows"></BaseTable>
      </div>
    </template>
    <template #saveButtonText> Add </template>
  </BaseModalEditCreate>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { useI18n } from 'vue-i18n';
import draggable from 'vuedraggable';
import { cva } from 'class-variance-authority';

import BaseTable from '../common/BaseTable.vue';
import BaseModalEditCreate from '../common/BaseModalEditCreate.vue';
import IconAddSmall from '../../assets/icons/IconAddSmall.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';

import {
  type PricingPolicyCatalogOrderHeader,
  type PricingPoliciesCatalogOrderDetail,
  type PriceCatalog,
} from '../../types/PricingTypes';

const emit = defineEmits(['detailAdded', 'orderChanged', 'detailDeleted']);

const { t: $t } = useI18n({ useScope: 'global' });

const priceCatalogsHeaders = [
  { value: 'code', text: 'Code', type: 'text', active: true },
  { value: 'name', text: 'Name', type: 'text', active: true },
];

const modalAddCatalog = ref(false);
const emitSelectedRows = ref(false);

const priceCatalogOrderDetailItems = ref<PricingPoliciesCatalogOrderDetail[]>([]);

const variantClass = (isDeleted: boolean | undefined) => {
  return cva(
    'flex items-center justify-between w-full mx-1 mt-1 transition rounded-md shadow-sm cursor-pointer mr-7 ',
    {
      variants: {
        mode: {
          edit: 'hover:mr-1 hover:pr-2 group',
          view: '',
        },
        deleted: {
          true: 'bg-red-100',
          false: 'bg-white',
          undefined: 'bg-white',
        },
      },
    }
  )({
    mode: props.mode,
    deleted: isDeleted,
  });
};

const props = defineProps<{
  mode: 'view' | 'edit';
  priceCatalogsList: PriceCatalog[];
  priceCatalogOrderDetails: PricingPoliciesCatalogOrderDetail[];
  priceCatalogOrder: PricingPolicyCatalogOrderHeader | undefined;
}>();

watch(
  () => props.priceCatalogOrderDetails,
  (newVal) => {
    priceCatalogOrderDetailItems.value = JSON.parse(
      JSON.stringify(
        newVal
          .filter((x) => x.pricingPolicyCatalogOrderHeaderId == props.priceCatalogOrder?.id)
          .sort((x, y) => x.sequence - y.sequence)
      )
    );
  }
);

function onCatalogsSelected(items: PriceCatalog[]) {
  modalAddCatalog.value = false;
  emitSelectedRows.value = false;
  items.forEach((catalog) => {
    if (
      !props.priceCatalogOrder ||
      priceCatalogOrderDetailItems.value
        .filter((x) => x.pricingPolicyCatalogOrderHeaderId == props.priceCatalogOrder?.id)
        .map((x) => x.priceCatalogId)
        .includes(catalog.id)
    )
      return;
    const newDetail: PricingPoliciesCatalogOrderDetail = {
      id: uuidv4(),
      pricingPolicyCatalogOrderHeaderId: props.priceCatalogOrder.id,
      priceCatalogId: catalog.id,
      priceCatalog: catalog,
      sequence: 0,
      isActive: true,
      isFromManager: true,
    };
    emit('detailAdded', newDetail);
  });
}

const unusedPriceCatalogs = computed(() => {
  const selectedCatalogsIds = new Set(
    priceCatalogOrderDetailItems.value.map((item) => item.priceCatalogId)
  );
  return props.priceCatalogsList.filter((x) => !selectedCatalogsIds.has(x.id));
});

function onDragEnd() {
  emit('orderChanged', priceCatalogOrderDetailItems.value);
}
</script>

<style>
.chosen {
  opacity: 1;
}
.chosen .bg-white {
  background-color: #e6eefe;
}
.chosen .bg-cyan-light {
  background-color: #c3d3f3;
}
</style>
